import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Trash2, Pencil, Users, Briefcase, FileText, DollarSign, Search, ExternalLink, Eye, Star, CheckCircle2, XCircle, RotateCcw, Wallet } from "lucide-react";
import DataSync from "@/components/DataSync";
import AdminUsers from "@/components/AdminUsers";
import AdminCompanies from "@/components/AdminCompanies";

const CATEGORIES = ["Programación", "Diseño", "Redacción", "Marketing", "Video & Audio", "IA & Datos"];
const STATUSES = ["all", "open", "in_progress", "completed", "closed"];
const STATUS_LABEL = {
  open: "Abierto", in_progress: "En curso", completed: "Completado", closed: "Cerrado", all: "Todos",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = async (project) => {
    setDetail({ project, proposals: [], messages: [], reviews: [], owner: null, accepted_freelancer: null });
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/projects/${project.project_id}/detail`);
      setDetail(data);
    } catch (e) { toast.error("Error cargando detalle"); }
    finally { setDetailLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch { toast.error("Sin acceso de administrador"); }
  };

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (q) params.q = q;
      const { data } = await api.get("/admin/projects", { params });
      setProjects(data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("No tienes permisos de administrador");
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) {
      fetchStats();
    }
  }, [user?.is_admin]);

  useEffect(() => {
    if (user?.is_admin) fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, q, user?.is_admin]);

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground text-sm">Cargando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-3">Acceso restringido</h1>
          <p className="text-muted-foreground mb-6">Esta sección es solo para administradores.</p>
          <Button onClick={() => navigate("/dashboard")} className="rounded-full" data-testid="admin-back-btn">Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  const openEdit = (project) => {
    setEditing(project);
    setEditForm({
      title: project.title, description: project.description,
      category: project.category, budget: project.budget,
      deadline_days: project.deadline_days, status: project.status,
      skills: (project.skills || []).join(", "),
    });
  };

  const saveEdit = async () => {
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        budget: parseFloat(editForm.budget),
        deadline_days: parseInt(editForm.deadline_days, 10),
        status: editForm.status,
        skills: editForm.skills.split(",").map(s => s.trim()).filter(Boolean),
      };
      await api.put(`/admin/projects/${editing.project_id}`, payload);
      toast.success("Proyecto actualizado");
      setEditing(null);
      // Wait for dialog close animation before refetching the table to avoid React removeChild race
      setTimeout(() => { fetchProjects(); fetchStats(); }, 250);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const deleteProject = async (id) => {
    try {
      await api.delete(`/admin/projects/${id}`);
      toast.success("Proyecto eliminado");
      // Delay refetch so AlertDialog can unmount cleanly first
      setTimeout(() => { fetchProjects(); fetchStats(); }, 250);
    } catch (e) { toast.error("Error"); }
  };

  // Admin project management actions
  const refreshDetail = async (project_id) => {
    try {
      const { data } = await api.get(`/admin/projects/${project_id}/detail`);
      setDetail(data);
    } catch { /* noop */ }
    fetchProjects();
    fetchStats();
  };

  const acceptProposal = async (proposal_id) => {
    try {
      await api.post(`/admin/proposals/${proposal_id}/accept`);
      toast.success("Propuesta aceptada. Proyecto en curso.");
      await refreshDetail(detail.project.project_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const rejectProposal = async (proposal_id) => {
    try {
      await api.post(`/admin/proposals/${proposal_id}/reject`);
      toast.success("Propuesta rechazada");
      await refreshDetail(detail.project.project_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const completeProject = async (project_id) => {
    try {
      await api.post(`/admin/projects/${project_id}/complete`);
      toast.success("Proyecto marcado como completado");
      await refreshDetail(project_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const creditFreelancer = async (project_id) => {
    try {
      const { data } = await api.post(`/admin/projects/${project_id}/credit-freelancer`);
      toast.success(`Freelancer acreditado ($${data.credited_amount_gross?.toFixed(0)} bruto)`);
      await refreshDetail(project_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const resetAssignment = async (project_id) => {
    try {
      await api.post(`/admin/projects/${project_id}/reset-assignment`);
      toast.success("Asignación revertida. Proyecto abierto de nuevo.");
      await refreshDetail(project_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const statCards = stats ? [
    { icon: Briefcase, label: "Proyectos", value: stats.total_projects, testid: "admin-stat-projects" },
    { icon: FileText, label: "Propuestas", value: stats.total_proposals, testid: "admin-stat-proposals" },
    { icon: Users, label: "Usuarios", value: stats.total_users, testid: "admin-stat-users" },
    { icon: DollarSign, label: "Valor completado", value: `$${stats.completed_value.toFixed(0)}`, testid: "admin-stat-value" },
  ] : [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-widest uppercase text-primary font-bold mb-2">Administración</div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">Panel de admin</h1>
            <p className="text-muted-foreground mt-2">Gestiona todos los proyectos, propuestas y usuarios de la plataforma.</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {statCards.map(s => (
            <Card key={s.label} data-testid={s.testid}>
              <CardContent className="p-6">
                <s.icon className="w-5 h-5 text-primary mb-3" />
                <div className="text-3xl font-heading font-bold tracking-tight">{s.value}</div>
                <div className="text-xs tracking-widest uppercase text-muted-foreground mt-1">{s.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Data sync (Export/Import between environments) */}
        <div className="mb-10">
          <DataSync />
        </div>

        {/* Main tabs: Projects | Users */}
        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="mb-6" data-testid="admin-main-tabs">
            <TabsTrigger value="projects" data-testid="admin-tab-projects">Proyectos</TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">Usuarios</TabsTrigger>
            <TabsTrigger value="companies" data-testid="admin-tab-companies">Empresas</TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
        {/* Filter */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar proyecto…" className="pl-10 h-11" data-testid="admin-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11 min-w-[180px] w-auto" data-testid="admin-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map(s => (
                <SelectItem key={s} value={s} data-testid={`admin-status-${s}`}>{STATUS_LABEL[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proyecto</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Presupuesto</TableHead>
                  <TableHead className="text-right">Propuestas</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingProjects ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Cargando…</TableCell></TableRow>
                ) : projects.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Sin proyectos</TableCell></TableRow>
                ) : (
                  projects.map(p => (
                    <TableRow key={p.project_id} data-testid={`admin-row-${p.project_id}`}>
                      <TableCell className="max-w-[280px]">
                        <div className="font-medium truncate">{p.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{p.description}</div>
                      </TableCell>
                      <TableCell><span className="text-xs px-2 py-1 rounded-sm bg-accent text-accent-foreground">{p.category}</span></TableCell>
                      <TableCell className="text-sm">{p.owner_name}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium">${p.budget?.toFixed(0)}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.proposals_count || 0}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] tracking-widest uppercase font-bold px-2 py-1 rounded-sm ${
                          p.status === 'open' ? 'bg-primary/10 text-primary' :
                          p.status === 'in_progress' ? 'bg-amber-500/10 text-amber-600' :
                          p.status === 'completed' ? 'bg-emerald-500/10 text-emerald-600' :
                          'bg-muted text-muted-foreground'
                        }`}>{STATUS_LABEL[p.status] || p.status}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openDetail(p)} title="Ver detalle completo" data-testid={`admin-detail-${p.project_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <a href={`/projects/${p.project_id}`} target="_blank" rel="noreferrer" data-testid={`admin-view-${p.project_id}`}>
                            <Button variant="ghost" size="icon" title="Abrir en nueva pestaña" type="button">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button variant="ghost" size="icon" title="Editar" onClick={() => openEdit(p)} data-testid={`admin-edit-${p.project_id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" title="Eliminar" className="text-destructive hover:text-destructive" data-testid={`admin-delete-${p.project_id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar proyecto?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esto también borrará todas sus propuestas, mensajes y reseñas. Esta acción no se puede deshacer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteProject(p.project_id)} data-testid={`admin-confirm-delete-${p.project_id}`}>
                                  Eliminar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="users">
            <AdminUsers />
          </TabsContent>

          <TabsContent value="companies">
            <AdminCompanies />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Editar proyecto</DialogTitle>
            <DialogDescription>Modifica los datos del proyecto seleccionado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Título</Label>
              <Input value={editForm.title || ""} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                className="mt-1.5 h-11" data-testid="admin-edit-title" />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Descripción</Label>
              <Textarea rows={4} value={editForm.description || ""} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                className="mt-1.5" data-testid="admin-edit-description" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs tracking-widest uppercase font-semibold">Categoría</Label>
                <Select value={editForm.category} onValueChange={v => setEditForm({ ...editForm, category: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="admin-edit-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-semibold">Estado</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="admin-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Abierto</SelectItem>
                    <SelectItem value="in_progress">En curso</SelectItem>
                    <SelectItem value="completed">Completado</SelectItem>
                    <SelectItem value="closed">Cerrado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-semibold">Presupuesto</Label>
                <Input type="number" value={editForm.budget || ""} onChange={e => setEditForm({ ...editForm, budget: e.target.value })}
                  className="mt-1.5 h-11" data-testid="admin-edit-budget" />
              </div>
              <div>
                <Label className="text-xs tracking-widest uppercase font-semibold">Días</Label>
                <Input type="number" value={editForm.deadline_days || ""} onChange={e => setEditForm({ ...editForm, deadline_days: e.target.value })}
                  className="mt-1.5 h-11" data-testid="admin-edit-deadline" />
              </div>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase font-semibold">Habilidades (coma-separadas)</Label>
              <Input value={editForm.skills || ""} onChange={e => setEditForm({ ...editForm, skills: e.target.value })}
                className="mt-1.5 h-11" data-testid="admin-edit-skills" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancelar</Button>
              <Button onClick={saveEdit} className="rounded-full" data-testid="admin-save-edit">Guardar cambios</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog with tabs */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{detail?.project?.title || "Detalle"}</DialogTitle>
            <DialogDescription>
              {detail?.project?.company_name ? `${detail.project.company_name} · ` : ""}
              {detail?.owner?.name}
              {detail?.owner?.email ? ` (${detail.owner.email})` : ""}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="text-sm text-muted-foreground py-10 text-center">Cargando…</div>
          ) : detail && (
            <Tabs defaultValue="info" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="info" data-testid="admin-tab-info">Detalle</TabsTrigger>
                <TabsTrigger value="proposals" data-testid="admin-tab-proposals">Propuestas ({detail.proposals.length})</TabsTrigger>
                <TabsTrigger value="chat" data-testid="admin-tab-chat">Chat ({detail.messages.length})</TabsTrigger>
                <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reseñas ({detail.reviews.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="mt-4 overflow-y-auto pr-2">
                <div className="space-y-4 text-sm">
                  {/* Action bar */}
                  <div
                    className="flex flex-wrap gap-2 p-3 rounded-md bg-muted/40 border"
                    data-testid="admin-project-actions"
                  >
                    <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mr-auto self-center">
                      Acciones rápidas
                    </div>
                    {detail.project.status !== "completed" && detail.project.status !== "closed" && (
                      <Button
                        size="sm" variant="outline"
                        className="rounded-full"
                        onClick={() => completeProject(detail.project.project_id)}
                        data-testid="admin-action-complete"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        Marcar completado
                      </Button>
                    )}
                    {detail.project.status === "in_progress" && detail.project.accepted_freelancer_id && !detail.project.admin_credited_at && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" className="rounded-full" data-testid="admin-action-credit">
                            <Wallet className="w-4 h-4 mr-1.5" />
                            Acreditar freelancer
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Acreditar pago al freelancer?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Se agregará ${detail.project.accepted_amount?.toFixed(0)} (bruto, menos comisión de plataforma) al wallet
                              de {detail.accepted_freelancer?.name} y el proyecto se marcará completado. Úsalo cuando el cliente ya pagó fuera de la plataforma.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => creditFreelancer(detail.project.project_id)}
                              data-testid="admin-action-credit-confirm"
                            >
                              Acreditar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                    {detail.project.accepted_freelancer_id && detail.project.status !== "completed" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="rounded-full text-destructive hover:text-destructive" data-testid="admin-action-reset">
                            <RotateCcw className="w-4 h-4 mr-1.5" />
                            Revertir asignación
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Revertir asignación?</AlertDialogTitle>
                            <AlertDialogDescription>
                              El proyecto volverá a "Abierto" y todas las propuestas se marcarán como "Pendientes" para que otro freelancer pueda ser aceptado.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => resetAssignment(detail.project.project_id)}
                              data-testid="admin-action-reset-confirm"
                            >
                              Revertir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div><div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">Categoría</div><div>{detail.project.category}</div></div>
                    <div><div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">Estado</div><div>{STATUS_LABEL[detail.project.status] || detail.project.status}</div></div>
                    <div><div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">Presupuesto</div><div className="font-semibold">${detail.project.budget?.toFixed(0)}</div></div>
                    <div><div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">Plazo</div><div>{detail.project.deadline_days} días · {detail.project.deadline_date}</div></div>
                    <div><div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">Cliente</div><div>{detail.owner?.name} <span className="text-muted-foreground">({detail.owner?.email})</span></div></div>
                    {detail.accepted_freelancer && (
                      <div><div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold">Freelancer asignado</div><div>{detail.accepted_freelancer.name} <span className="text-muted-foreground">({detail.accepted_freelancer.email})</span></div></div>
                    )}
                  </div>
                  {(detail.project.skills || []).length > 0 && (
                    <div>
                      <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold mb-2">Habilidades</div>
                      <div className="flex flex-wrap gap-1.5">
                        {detail.project.skills.map(s => <span key={s} className="text-xs px-2 py-1 rounded-sm bg-accent text-accent-foreground">{s}</span>)}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground font-bold mb-2">Descripción completa</div>
                    <div className="text-sm whitespace-pre-line leading-relaxed border-l-2 border-primary/40 pl-4 py-1">
                      {detail.project.description}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="proposals" className="mt-4 overflow-y-auto pr-2">
                {detail.proposals.length === 0 ? (
                  <div className="text-center text-muted-foreground text-sm py-10">Sin propuestas.</div>
                ) : (
                  <div className="space-y-3">
                    {detail.proposals.map(pr => {
                      const isAccepted = pr.status === "accepted";
                      const isRejected = pr.status === "rejected";
                      const canAct = detail.project.status === "open" || (detail.project.status === "in_progress" && !isAccepted && !isRejected);
                      return (
                      <div key={pr.proposal_id} className="border border-border rounded-md p-4 bg-card" data-testid={`admin-detail-proposal-${pr.proposal_id}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-8 h-8"><AvatarImage src={pr.freelancer_picture} /><AvatarFallback>{pr.freelancer_name?.[0]||'F'}</AvatarFallback></Avatar>
                            <div>
                              <div className="font-medium text-sm">{pr.freelancer_name}</div>
                              <div className="text-[11px] text-muted-foreground">
                                {pr.days_to_deliver} días ·{" "}
                                <span className={
                                  isAccepted ? "text-emerald-600 font-semibold" :
                                  isRejected ? "text-destructive" :
                                  "text-amber-600"
                                }>
                                  {isAccepted ? "Aceptada" : isRejected ? "Rechazada" : "Pendiente"}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-lg font-heading font-bold">${pr.amount?.toFixed(0)}</div>
                        </div>
                        <p className="text-sm text-muted-foreground">{pr.message}</p>
                        {pr.milestones && <div className="mt-2 text-xs whitespace-pre-line"><strong>Hitos:</strong> {pr.milestones}</div>}
                        {!isAccepted && !isRejected && detail.project.status === "open" && (
                          <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" className="rounded-full" data-testid={`admin-accept-${pr.proposal_id}`}>
                                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                                  Aceptar propuesta
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>¿Aceptar propuesta de {pr.freelancer_name}?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    El proyecto pasará a "En curso" con {pr.freelancer_name} como freelancer asignado (${pr.amount?.toFixed(0)}). Las demás propuestas se rechazarán automáticamente.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => acceptProposal(pr.proposal_id)}
                                    data-testid={`admin-accept-confirm-${pr.proposal_id}`}
                                  >
                                    Aceptar
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            <Button
                              size="sm" variant="outline" className="rounded-full text-destructive hover:text-destructive"
                              onClick={() => rejectProposal(pr.proposal_id)}
                              data-testid={`admin-reject-${pr.proposal_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1.5" />
                              Rechazar
                            </Button>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="chat" className="mt-4 overflow-y-auto pr-2">
                {detail.messages.length === 0 ? (
                  <div className="text-center text-muted-foreground text-sm py-10">Sin mensajes.</div>
                ) : (
                  <div className="space-y-2">
                    {detail.messages.map(m => (
                      <div key={m.message_id} className="border border-border rounded-md p-3 bg-card" data-testid={`admin-detail-msg-${m.message_id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-xs font-semibold">{m.sender_name}</div>
                          <div className="text-[10px] text-muted-foreground">{new Date(m.created_at).toLocaleString()}</div>
                        </div>
                        {m.text && <div className="text-sm whitespace-pre-line">{m.text}</div>}
                        {m.attachment && (
                          <div className="mt-2 flex items-center gap-2 p-2 rounded-sm bg-muted text-xs">
                            <FileText className="w-3.5 h-3.5" />
                            <span className="font-medium truncate flex-1">{m.attachment.original_filename}</span>
                            <span className="text-muted-foreground">{(m.attachment.size/1024).toFixed(1)} KB</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="mt-4 overflow-y-auto pr-2">
                {detail.reviews.length === 0 ? (
                  <div className="text-center text-muted-foreground text-sm py-10">Sin reseñas.</div>
                ) : (
                  <div className="space-y-3">
                    {detail.reviews.map(r => (
                      <div key={r.review_id} className="border border-border rounded-md p-4 bg-card" data-testid={`admin-detail-review-${r.review_id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex gap-0.5">
                            {[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'fill-primary text-primary' : 'text-muted-foreground'}`} />)}
                          </div>
                          <span className="text-sm font-medium">{r.from_user_name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{r.comment}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
